import React from 'react';
import {
    IonList,
    IonItem,
    IonLabel,
    IonButton,
    IonButtons,
    IonIcon,
    IonSelect,
    IonSelectOption,
    IonFooter,
    IonToolbar,
    IonItemSliding,
    IonItemOptions,
    IonItemOption,
    IonText,
} from '@ionic/react';
import { trashOutline, personOutline, calendarOutline, timeOutline } from 'ionicons/icons';
import Page from '../../../components/core/Page';
import { Profile, CartItem } from '../../../models';
import CurrencyPrefix from "../../../components/financials/currency-prefix";
import {Customer} from "../../../models";
import { Ages } from '../../../utils/ages';
import { resolveProfileAllocation, allowsGuest } from '../../../utils/productPresentation';
import { formatMoney } from '../../../utils/currency';
import { QuantityStepper } from '../../../components/common/QuantityStepper/QuantityStepper';

interface Props {
    customer: Customer|null;
    cart: CartItem[];
    profiles: Profile[];
    total: number;
    isFetching?: boolean;
    paymentUrl?: string;
    proforma?: any;
    payLaterAllowed: boolean;
    paymentProviderConfigured: boolean;
    onUpdateQuantity: (id: string, quantity: number) => void;
    onRemoveItem: (id: string) => void;
    onUpdateLearner: (id: string, learnerId: number | null) => void;
    onClearCart: () => void;
    onCheckout: () => void;
    onSubmitWithoutPayment: () => void;
}

export const CheckoutPage: React.FC<Props> = ({
    customer,
    cart,
    profiles,
    total,
    isFetching,
    paymentUrl,
    proforma,
    payLaterAllowed,
    paymentProviderConfigured,
    onUpdateQuantity,
    onRemoveItem,
    onUpdateLearner,
    onClearCart,
    onCheckout,
    onSubmitWithoutPayment,
}) => {
    const formatEventDate = (meta: any) => {
        if (!meta.eventStartDateTs) return undefined;
        const start = new Ages(meta.eventStartDateTs).toFormat('dd mmm yyyy');
        if (!meta.eventEndDateTs || meta.eventStartDateTs === meta.eventEndDateTs) return start;
        const end = new Ages(meta.eventEndDateTs).toFormat('dd mmm yyyy');
        if (start === end) return start;
        return `${start} – ${end}`;
    };

    const formatEventTime = (meta: any) => {
        if (!meta.eventStartDateTs) return undefined;
        if (!meta.eventEndDateTs || meta.eventStartDateTs === meta.eventEndDateTs) return undefined;
        const startTime = new Ages(meta.eventStartDateTs).toFormat('hh:ii');
        const endTime = new Ages(meta.eventEndDateTs).toFormat('hh:ii');
        if (startTime === '00:00' && endTime === '00:00') return undefined;
        return `${startTime} – ${endTime}`;
    };

    return (
        <Page
            title="Checkout"
            showMenuButton={true}
            showBackButton={false}
            backRouterLink="/my-shop"
            showLoading={isFetching}
            footer={
                <IonFooter>
                    <IonToolbar color="primary">
                        <div slot="start" style={{ paddingLeft: '16px', fontWeight: 'bold' }}>
                            Total: <CurrencyPrefix customer={customer} /><span data-testid="checkout-total">{formatMoney(total)}</span>
                        </div>
                        <IonButtons slot="end">
                            {paymentProviderConfigured && (
                                <IonButton
                                    fill="clear"
                                    onClick={onCheckout}
                                    disabled={isFetching || cart.length === 0}
                                    color="light"
                                >
                                    <span style={{ color: 'white' }}>Pay now</span>
                                </IonButton>
                            )}
                            {payLaterAllowed && (
                                <IonButton
                                    fill="clear"
                                    onClick={onSubmitWithoutPayment}
                                    disabled={isFetching || cart.length === 0}
                                    color="light"
                                >
                                    <span style={{ color: 'white' }}>
                                        {paymentProviderConfigured ? 'Pay later' : 'Submit order (pay later)'}
                                    </span>
                                </IonButton>
                            )}
                            {!paymentProviderConfigured && !payLaterAllowed && (
                                <IonButton fill="clear" disabled color="light">
                                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>Checkout unavailable</span>
                                </IonButton>
                            )}
                        </IonButtons>
                    </IonToolbar>
                </IonFooter>
            }
        >
            {cart.length === 0 ? (
                <div className="ion-text-center ion-padding">
                    {paymentUrl ? (
                        <>
                            <p>Redirecting to payment provider...</p>

                            {proforma && (
                                <div className="ion-margin-bottom" style={{ border: '1px solid var(--ion-color-light-shade)', borderRadius: '8px', padding: '16px', background: 'var(--ion-color-light)', textAlign: 'left' }}>
                                    {proforma.lines.map((line: any) => (
                                        <div key={line.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.9rem' }}>
                                            <span>{line.description} (x{Math.floor(parseFloat(line.quantity))})</span>
                                            <span><CurrencyPrefix customer={customer} />{line.totalDisplay}</span>
                                        </div>
                                    ))}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--ion-color-light-shade)', marginTop: '12px', paddingTop: '12px', fontWeight: 'bold' }}>
                                        <span>Total</span>
                                        <span><CurrencyPrefix customer={customer} />{proforma.totalDisplay}</span>
                                    </div>
                                </div>
                            )}

                            <IonButton href={paymentUrl} expand="block" color="success" className="ion-margin-top">
                                Proceed to Payment
                            </IonButton>
                            <p className="ion-text-center"><small>If you are not redirected automatically, please click the button above.</small></p>
                        </>
                    ) : (
                        <>
                            <p>Your basket is empty.</p>
                            <IonButton routerLink="/my-shop" expand="block" fill="clear">
                                Back to Shop
                            </IonButton>
                        </>
                    )}
                </div>
            ) : (
                <>
                    <IonList>
                        {cart.map((item) => {
                            return (
                                <IonItemSliding key={item.id}>
                                    <IonItem>
                                        <IonLabel>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <h2 style={{ margin: 0 }}>{item.product.name}</h2>
                                            </div>
                                            {item.product.meta.eventName && (
                                                <p style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)' }}>{item.product.meta.eventName}</p>
                                            )}
                                            {item.product.meta.isEventTicket && (
                                                <div style={{ marginTop: '8px' }}>
                                                    {formatEventDate(item.product.meta) && (
                                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                                            <IonIcon icon={calendarOutline} style={{ marginRight: '8px' }} />
                                                            <IonText color="medium">
                                                                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                                                                    {formatEventDate(item.product.meta)}
                                                                    {formatEventTime(item.product.meta) && `, ${formatEventTime(item.product.meta)}`}
                                                                </p>
                                                            </IonText>
                                                        </div>
                                                    )}
                                                    {item.product.meta.slots && item.product.meta.slots.length > 1 && (
                                                        <div style={{ marginTop: '4px' }}>
                                                            {item.product.meta.slots.map((slot: any, index: number) => (
                                                                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                                                                    <IonIcon icon={timeOutline} style={{ marginRight: '8px' }} />
                                                                    <IonText color="medium">
                                                                        <p style={{ margin: 0, fontSize: '0.9rem' }}>{slot.displayTime}</p>
                                                                    </IonText>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {(item.product.meta.eventDescription || item.product.description) && (
                                                <div style={{ fontSize: '0.9rem', marginTop: '8px', color: 'var(--ion-color-medium)' }}>
                                                    {item.product.meta.eventDescription || item.product.description}
                                                </div>
                                            )}
                                            <p><CurrencyPrefix customer={customer} />
                                                {item.product.priceDisplay} each
                                            </p>
                                            {(() => {
                                                const allocation = resolveProfileAllocation(item.product);
                                                if (allocation === 'none') return null;
                                                const guestAllowed = allowsGuest(allocation);
                                                return (
                                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                                                        <IonIcon icon={personOutline} style={{ marginRight: '4px' }} size="small" />
                                                        <IonSelect
                                                            interface="popover"
                                                            value={item.learnerId}
                                                            placeholder={guestAllowed ? "Guest Ticket" : "Assign Profile"}
                                                            onIonChange={(e) => onUpdateLearner(item.id, e.detail.value)}
                                                            style={{ fontSize: '0.9rem', '--padding-start': '0' }}
                                                        >
                                                            {guestAllowed && (
                                                                <IonSelectOption value={null}>Guest / No Profile</IonSelectOption>
                                                            )}
                                                            {profiles.map((profile) => (
                                                                <IonSelectOption key={profile.id} value={profile.id}>
                                                                    {profile.firstName} {profile.lastName}
                                                                </IonSelectOption>
                                                            ))}
                                                        </IonSelect>
                                                    </div>
                                                );
                                            })()}
                                        </IonLabel>
                                        <div slot="end">
                                            <QuantityStepper
                                                quantity={item.quantity}
                                                onDecrement={() => onUpdateQuantity(item.id, item.quantity - 1)}
                                                onIncrement={() => onUpdateQuantity(item.id, item.quantity + 1)}
                                            />
                                        </div>
                                    </IonItem>

                                    <IonItemOptions side="end">
                                        <IonItemOption color="danger" onClick={() => onRemoveItem(item.id)}>
                                            <IonIcon slot="icon-only" icon={trashOutline} />
                                        </IonItemOption>
                                    </IonItemOptions>
                                </IonItemSliding>
                            );
                        })}

                        <IonItem lines="none" className="ion-margin-top">
                            <IonButton routerLink="/my-shop" expand="block" fill="clear">
                                Back to Shop
                            </IonButton>
                            <IonButton color="danger" fill="clear" onClick={onClearCart} slot="end">
                                Clear Basket
                            </IonButton>
                        </IonItem>
                    </IonList>

                    {paymentProviderConfigured && (
                        <IonButton
                            expand="block"
                            onClick={onCheckout}
                            disabled={isFetching || cart.length === 0}
                            color="success"
                        >
                            Pay now
                        </IonButton>
                    )}
                    {payLaterAllowed && (
                        <IonButton
                            expand="block"
                            onClick={onSubmitWithoutPayment}
                            disabled={isFetching || cart.length === 0}
                            color={paymentProviderConfigured ? 'medium' : 'success'}
                        >
                            {paymentProviderConfigured ? 'Pay later' : 'Submit order (pay later)'}
                        </IonButton>
                    )}
                    {!paymentProviderConfigured && !payLaterAllowed && (
                        <p className="ion-text-center ion-padding" style={{ color: 'var(--ion-color-danger)' }}>
                            Checkout is currently unavailable.
                        </p>
                    )}
                </>
            )}
        </Page>
    );
};
